import { Box, CircularProgress, Grid } from '@material-ui/core';
import { CheckoutSession, LenderName, PaymentMethodType } from '@nemuru-eng/js-api-client';

import { LenderIcons } from '@/components/modules/Application/components/PaymentMethod/_shared/components/LenderIcons';
import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { CheckoutSessionEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionEntity';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { PaymentMethodFactory } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/PaymentMethodFactory';
import { CheckoutSessionSummary } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/CheckoutSessionSummary';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { CustomTab, CustomTabs } from '@/components/shared/components/CustomTabs/CustomTabs';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { IconByAllowedPaymentMethodType } from '@/components/shared/components/PricingsTable/constants/IconByAllowedPaymentMethodType';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { ProviderName } from '@/types/ProviderName';

interface PaymentMethodsProps {
  checkoutSession: CheckoutSession;
}

export const PaymentMethods = ({ checkoutSession }: PaymentMethodsProps) => {
  const { refreshing, checkoutSessionEntity, paymentMethodDetailEntities, selectedPaymentMethodType, update } =
    useCheckoutSessionContext();

  const { md } = useScreen();
  const { palette } = useCustomTheme();

  const hasFinalStatus = CheckoutSessionEntity.hasFinalStatus(checkoutSession.status);
  const providers = getProviders(paymentMethodDetailEntities);

  const tabs: CustomTab[] = checkoutSession.paymentMethods
    .sort((a, b) => a.type.localeCompare(b.type))
    .map((paymentMethod) => {
      const currentCheckoutSession = checkoutSessionEntity?.checkoutSession ?? checkoutSession;
      const disabled = hasFinalStatus && paymentMethod.type !== currentCheckoutSession.selectedPaymentMethodType;
      const selected = paymentMethod.type === currentCheckoutSession.selectedPaymentMethodType;
      const currentTab = paymentMethod.type === selectedPaymentMethodType;
      const currentProviders = providers.get(paymentMethod.type);

      return {
        label: (
          <CustomPaper fill="paper" style={{ padding: '6px 12px', boxShadow: 'none' }}>
            <Box
              display="flex"
              alignItems="center"
              gridGap={8}
              color={currentTab ? palette.text.title : palette.text.primary}
            >
              <Icon size={16}>{IconByAllowedPaymentMethodType(paymentMethod.type)}</Icon>
              <Font variant="body2" color="textPrimary" className="overflow-text-ellipsis">
                <Text tId={`${paymentMethod.type}_TAB_LABEL`} />
              </Font>
              {currentProviders && (
                <LenderIcons
                  size={16}
                  collapseAfter={2}
                  collapseOnlyOnLargeScreen={false}
                  allLenderNames={currentProviders as LenderName[]}
                  activeLenderNames={currentProviders as LenderName[]}
                />
              )}
              {selected && (
                <Icon size={16} color={palette.primary.main}>
                  push_pin
                </Icon>
              )}
            </Box>
          </CustomPaper>
        ),
        name: paymentMethod.type,
        tabProps: {
          disabled,
          style: { opacity: disabled ? 0.5 : 1, padding: '8px 8px 12px', minWidth: 0 },
        },
        panel: (
          <Box key={`tab-${paymentMethod.type}`} position="relative">
            <Grid
              container
              justifyContent={'space-between'}
              spacing={md ? 5 : 3}
              className={refreshing ? 'pulse-opacity' : undefined}
            >
              <Grid item xs={12} md={7} lg={8}>
                <PaymentMethodFactory paymentMethodType={paymentMethod.type} />
              </Grid>
              <Grid item xs={12} md={5} lg={4}>
                <Box position={'sticky'} top={197}>
                  <CheckoutSessionSummary
                    checkoutSessionDetailEntity={checkoutSessionEntity}
                    paymentMethodType={paymentMethod.type}
                  />
                </Box>
              </Grid>
            </Grid>
            {refreshing && (
              <Box
                p={4}
                position={'fixed'}
                top={0}
                left={0}
                right={0}
                bottom={0}
                height="100%"
                width="100%"
                zIndex={10}
              >
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CustomPaper fill="paper" style={{ padding: '16px 24px' }} boxProps={{ width: 'fit-content' }}>
                    <FlexRow gridGap={16}>
                      <CircularProgress size={18} />
                      <Font variant="subtitle2">
                        <Text tId="UPDATING" />
                      </Font>
                    </FlexRow>
                  </CustomPaper>
                </Box>
              </Box>
            )}
          </Box>
        ),
      };
    });

  const handleValueChange = (newValue: string) => {
    update({ selectedPaymentMethodType: newValue as PaymentMethodType });
  };

  return (
    <CustomTabs
      defaultValue={checkoutSession.selectedPaymentMethodType ?? undefined}
      value={selectedPaymentMethodType}
      tabs={tabs}
      tabClassProps={{ tabPanel: { root: { marginTop: '-26px' } } }}
      onValueChange={handleValueChange}
    />
  );
};

const getProviders = (paymentMethodDetailEntities?: PaymentMethodDetailEntity[]) => {
  const providersMap = new Map<PaymentMethodType, ProviderName[]>();
  if (!paymentMethodDetailEntities) {
    return providersMap;
  }
  paymentMethodDetailEntities.forEach((entity) => {
    providersMap.set(entity.type, entity.paymentMethod?.getAllProviderNames() ?? []);
  });
  return providersMap;
};
